/**
 * Note lane header (W2-03 note-lane; DESIGN.md §4.6 "Line [Main v] Also [Orbit A x] [+] sides [both v] X grid [11 v]
 * {magnet} zoom [-][+] {crosshair} fit"). Rendered by the timeline host's header slot of each lane instance (the
 * Chart timeline panel portals it).
 *
 *   active lane   row 1: line picker, pin toggle, +X direction arrow (turns with the line at the playhead; the
 *                 tooltip says what it means), fit; row 2: "also show" chips with an add menu, the sides filter, the
 *                 X grid count with its magnet, lane X zoom.
 *   pinned lane   row 1: the line's name, "Edit this line", unpin; row 2: the same X controls (shared X view).
 * The +X arrow is updated through a ref from an 'overlay' frame callback (every frame while playing, on request while
 * paused; no React state per frame).
 */

import { useEffect, useMemo, useRef, type ComponentType, type RefObject } from 'react';
import type { LineId } from '../../core/model/types';
import type { AppServices } from '../../app/registry/types';
import { useT } from '../../i18n/useT';
import { Icon } from '../../ui/Icon';
import { IconButton } from '../../ui/IconButton';
import { Menu } from '../../ui/Menu';
import { Select } from '../../ui/Select';
import { Tooltip } from '../../ui/Tooltip';
import type { MenuItem } from '../../ui/types';
import { ACTIVE_LANE_ID, lineOfLaneId } from '../../render/timeline/laneColumn';
import { useDocSelect, useEditorSelect, useSession, useStore, sameArray } from './hooks';
import { m } from './noteLane.messages';
import { hostOf, laneSession, playheadSec, ref, setSides, type LaneSides } from './laneSession';
import s from './LaneHeader.module.css';

/** The sides filter's choices: which notes the lane shows (both sides of the line, above only, below only). */
const SIDE_CHOICES: ReadonlyArray<{ readonly value: LaneSides; readonly icon: string; readonly label: string }> = [
  { value: 'both', icon: 'ArrowDownUp', label: m.sidesBoth },
  { value: 'above', icon: 'ArrowUpToLine', label: m.sidesAbove },
  { value: 'below', icon: 'ArrowDownToLine', label: m.sidesBelow },
];

interface LineOption {
  readonly id: LineId;
  readonly label: string;
  readonly uiMover: boolean;
}

function sameOptions(a: readonly LineOption[], b: readonly LineOption[]): boolean {
  return (
    a.length === b.length &&
    a.every((x, i) => x.id === b[i]?.id && x.label === b[i].label && x.uiMover === b[i].uiMover)
  );
}

/** Lines in export order with display labels. */
function useLineOptions(services: AppServices): readonly LineOption[] {
  const t = useT();
  const store = useStore(services);
  const select = useMemo(
    () =>
      (
        doc: NonNullable<AppServices['store']>['doc'],
        idx: NonNullable<AppServices['store']>['idx'],
      ): readonly LineOption[] =>
        doc.lineOrder.map((id) => {
          const rec = doc.lines.get(id);
          const name = rec?.name.trim() ?? '';
          return {
            id,
            label: name === '' ? t(m.lineUnnamed, { index: idx.lineIndex(id) }) : name,
            uiMover: (rec?.attachUI ?? null) !== null,
          };
        }),
    [t],
  );
  return useDocSelect(store, ['lines', 'lineOrder'], select, [], sameOptions);
}

/** A ref rotated to the on-screen direction of +X of `line` at the playhead (frame callback, no React state). */
function usePlusXArrow(services: AppServices, line: LineId | null, id: string): RefObject<HTMLSpanElement | null> {
  const el = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const node = el.current;
    if (node === null) return undefined;
    let last = Number.NaN;
    const frameId = `note-lane:plusx:${id}`;
    const update = (): void => {
      const engine = services.engine;
      let deg = 0;
      if (engine !== null && line !== null) {
        try {
          const rot = engine.lineState(line, playheadSec(services)).rot;
          deg = Number.isFinite(rot) ? rot : 0;
        } catch {
          deg = 0;
        }
      }
      if (deg === last) return;
      last = deg;
      // World angles are counter-clockwise; CSS rotates clockwise.
      node.style.transform = `rotate(${String(-deg)}deg)`;
    };
    update();
    // 'overlay' runs every frame while playing (the line turns during playback); paused, it runs on request
    // (seeks, edits). 'idle' would only run on request, so the arrow would stand still during playback.
    const offFrame = services.scheduler.add(frameId, 'overlay', update);
    const request = (): void => {
      services.scheduler.request(frameId);
    };
    const offTransport = services.audio.transport.onChange(request);
    const store = services.store;
    const offStore = store === null ? null : store.subscribe(request);
    return () => {
      offFrame();
      offTransport();
      offStore?.();
    };
  }, [services, line, id]);
  return el;
}

export interface LaneHeaderProps {
  readonly services: AppServices;
  readonly instanceId: string;
}

export function LaneHeader(props: LaneHeaderProps) {
  const { services, instanceId } = props;
  const t = useT();
  const session = useSession(services);
  const lines = useLineOptions(services);
  const isActive = instanceId === ACTIVE_LANE_ID;
  const activeLine = useEditorSelect(services, (st) => st.activeLine);
  const pinned = useEditorSelect(services, (st) => st.pinnedLanes, sameArray);
  const alsoShow = useEditorSelect(services, (st) => st.alsoShow, sameArray);
  const line = isActive ? activeLine : lineOfLaneId(instanceId);
  const arrow = usePlusXArrow(services, line, instanceId);

  const nameOf = (id: LineId): string => lines.find((l) => l.id === id)?.label ?? t(m.noLine);
  const editor = (): ReturnType<AppServices['editor']['getState']> => services.editor.getState();
  const relayout = (): void => {
    hostOf(laneSession(services))?.relayout();
  };

  // The X grid count and X snap live in the top bar's snap controls (one place for one setting, UX-041); the lane
  // keeps its own X zoom.
  const xControls = (
    <span className={s.group}>
      <IconButton icon="ZoomOut" size="sm" label={ref(m.zoomXOut)} command="lane.zoomXOut" />
      <IconButton icon="ZoomIn" size="sm" label={ref(m.zoomXIn)} command="lane.zoomXIn" />
    </span>
  );

  if (!isActive) {
    return (
      <div className={s.header} data-lane-header={instanceId}>
        <div className={s.row}>
          <span className={s.lineName} title={line === null ? '' : nameOf(line)}>
            {line === null ? t(m.noLine) : nameOf(line)}
          </span>
          <Tooltip content={t(m.plusX)}>
            <span className={s.arrow} ref={arrow} role="img" aria-label={t(m.plusX)}>
              <Icon name="ArrowRight" size={14} />
            </span>
          </Tooltip>
          <IconButton
            icon="PencilLine"
            size="sm"
            label={ref(m.makeActive)}
            onClick={() => {
              if (line !== null) editor().setActiveLine(line);
            }}
          />
          <IconButton
            icon="PinOff"
            size="sm"
            label={ref(m.unpinLane)}
            onClick={() => {
              editor().setPinnedLanes(pinned.filter((l) => l !== line));
              relayout();
            }}
          />
        </div>
        <div className={s.row}>{xControls}</div>
      </div>
    );
  }

  const alsoCandidates: MenuItem[] = lines
    .filter((l) => l.id !== activeLine && !alsoShow.includes(l.id))
    .map((l) => ({
      kind: 'action' as const,
      label: l.label,
      onSelect: () => {
        editor().setAlsoShow([...editor().alsoShow, l.id]);
      },
    }));
  const isPinned = line !== null && pinned.includes(line);
  const sides: LaneSides = session.sides;

  return (
    <div className={s.header} data-lane-header={instanceId}>
      <div className={s.row}>
        <span className={`${s.compact} ${s.grow}`}>
          <Select
            ariaLabel={t(m.headerLinePick)}
            value={line === null ? '' : String(line)}
            options={lines.map((l) => ({
              value: String(l.id),
              label: l.label,
              ...(l.uiMover ? { icon: 'Gauge' } : {}),
            }))}
            onChange={(v) => {
              const id = Number(v) as LineId;
              editor().setActiveLine(id);
            }}
          />
        </span>
        <IconButton icon="Plus" size="sm" label={ref(m.addLine)} command="lines.add" />
        <Tooltip content={t(line === null ? m.plusXNone : m.plusX)}>
          <span className={s.arrow} ref={arrow} role="img" aria-label={t(m.plusX)}>
            <Icon name="ArrowRight" size={14} />
          </span>
        </Tooltip>
        <IconButton
          icon={isPinned ? 'PinOff' : 'Pin'}
          size="sm"
          label={ref(isPinned ? m.unpinLane : m.pinLane)}
          pressed={isPinned}
          disabledReason={line === null ? ref(m.reasonNoLine) : null}
          onClick={() => {
            if (line === null) return;
            editor().setPinnedLanes(isPinned ? pinned.filter((l) => l !== line) : [...pinned, line]);
            relayout();
          }}
        />
        <IconButton icon="Crosshair" size="sm" label={ref(m.fitX)} command="lane.fitX" />
      </div>
      <div className={s.row}>
        <span className={s.group} aria-label={t(m.headerAlso)} role="group">
          <span className={s.caption}>{t(m.headerAlso)}</span>
          {alsoShow.map((id) => (
            <span key={id} className={s.chip}>
              <span className={s.chipText}>{nameOf(id)}</span>
              <button
                type="button"
                className={s.chipRemove}
                aria-label={t(m.headerAlsoRemove, { line: nameOf(id) })}
                title={t(m.headerAlsoRemove, { line: nameOf(id) })}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={() => {
                  editor().setAlsoShow(editor().alsoShow.filter((l) => l !== id));
                }}
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          ))}
          <Menu
            trigger={
              <IconButton
                icon="Plus"
                size="sm"
                label={ref(m.headerAlsoAdd)}
                disabledReason={alsoCandidates.length === 0 ? ref(m.reasonNoOtherLine) : null}
              />
            }
            items={alsoCandidates}
          />
        </span>
        <span className={s.spacer} />
        {/* Sides as icon buttons with tooltips (UX-041: the select was squeezed to "Both sid"). */}
        <span className={s.segmented} role="group" aria-label={t(m.headerSides)}>
          {SIDE_CHOICES.map((c) => (
            <IconButton
              key={c.value}
              icon={c.icon}
              size="sm"
              label={ref(c.label)}
              pressed={sides === c.value}
              onClick={() => {
                setSides(laneSession(services), c.value);
              }}
            />
          ))}
        </span>
        {xControls}
      </div>
    </div>
  );
}

const headers = new WeakMap<AppServices, ComponentType<{ readonly instanceId: string }>>();

/** The header component bound to a services object (column instances carry a component, not an element). */
export function laneHeaderFor(services: AppServices): ComponentType<{ readonly instanceId: string }> {
  const hit = headers.get(services);
  if (hit !== undefined) return hit;
  const Bound = (p: { readonly instanceId: string }) => <LaneHeader services={services} instanceId={p.instanceId} />;
  Bound.displayName = 'LaneHeader';
  headers.set(services, Bound);
  return Bound;
}
